import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScanLine, Package, Plus } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';
import { supabase } from '@/integrations/supabase/client';
import { useCompany } from '@/context/CompanyContext';
import { toast } from 'sonner';

interface ProductInfo {
  id: string;
  name: string;
  quantity: number;
  price: number;
  image_url: string | null;
  barcode: string | null;
  isGeneric?: boolean;
  brand?: string;
}

// Detect iOS device
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

export const FloatingScannerButton: React.FC = () => {
  const [product, setProduct] = useState<ProductInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addQuantity, setAddQuantity] = useState('1');
  const [addPrice, setAddPrice] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const { currentCompany } = useCompany();
  
  // html5-qrcode ref (Android)
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  // ZXing ref (iOS)
  const zxingReaderRef = useRef<BrowserMultiFormatReader | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanningRef = useRef<boolean>(false);
  const lastScanRef = useRef<string>('');
  
  const scannerContainerId = 'floating-scanner-container';

  const stopScanner = useCallback(async () => {
    scanningRef.current = false;
    
    // Stop html5-qrcode (Android)
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) {
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch (error) {
        console.error('Error stopping html5-qrcode:', error);
      }
      html5QrCodeRef.current = null;
    }
    
    // Stop media stream (iOS)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    zxingReaderRef.current = null;
  }, []);

  const handleScan = useCallback(async (barcode: string) => {
    // Prevent duplicate scans
    if (barcode === lastScanRef.current) return;
    lastScanRef.current = barcode;
    
    await stopScanner();
    setIsScanning(false);
    setIsLoading(true);
    setShowResult(true);
    
    try {
      if (!currentCompany?.id) {
        toast.error('Aucune entreprise sélectionnée');
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('products')
        .select('id, name, quantity, price, image_url, barcode')
        .eq('company_id', currentCompany.id)
        .eq('barcode', barcode)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setProduct(data);
        toast.success(`Produit trouvé: ${data.name}`);
      } else {
        toast.info('Recherche dans OpenFoodFacts...');
        
        const { data: lookupData, error: lookupError } = await supabase.functions.invoke('barcode-lookup', {
          body: { barcode }
        });

        if (lookupError) {
          console.error('Erreur barcode-lookup:', lookupError);
          toast.error('Produit non trouvé');
          setProduct(null);
        } else if (lookupData?.found && lookupData?.unified) {
          // Use unified response from barcode-lookup API
          const genericProduct: ProductInfo = {
            id: '',
            name: lookupData.unified.title || 'Produit inconnu',
            quantity: 0,
            price: 0,
            image_url: lookupData.unified.images?.[0] || null,
            barcode: barcode,
            isGeneric: true,
            brand: lookupData.unified.brand || undefined
          };
          setProduct(genericProduct);
          toast.success(`Produit trouvé: ${genericProduct.name}`);
        } else {
          toast.info(`Produit non trouvé pour le code: ${barcode}`);
          setProduct(null);
        }
      }
    } catch (error) {
      console.error('Erreur recherche produit:', error);
      toast.error('Erreur lors de la recherche du produit');
    } finally {
      setIsLoading(false);
    }
  }, [currentCompany?.id, stopScanner]);

  // Rotate image 90 degrees clockwise
  const rotateImage90 = useCallback((canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): ImageData => {
    const width = canvas.width;
    const height = canvas.height;
    const imageData = ctx.getImageData(0, 0, width, height);
    
    // Create rotated canvas
    const rotatedCanvas = document.createElement('canvas');
    rotatedCanvas.width = height;
    rotatedCanvas.height = width;
    const rotatedCtx = rotatedCanvas.getContext('2d')!;
    
    // Draw rotated
    rotatedCtx.translate(height / 2, width / 2);
    rotatedCtx.rotate(Math.PI / 2);
    rotatedCtx.drawImage(canvas, -width / 2, -height / 2);
    
    return rotatedCtx.getImageData(0, 0, height, width);
  }, []);

  const startZXingScanner = useCallback(async () => {
    try {
      const videoElement = videoRef.current;
      const canvas = canvasRef.current;
      if (!videoElement || !canvas) {
        console.error('Video or canvas element not found');
        return;
      }

      // Configure ZXing with optimized hints for iOS
      const hints = new Map();
      hints.set(DecodeHintType.TRY_HARDER, true);
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.EAN_13,
        BarcodeFormat.EAN_8,
        BarcodeFormat.UPC_A,
        BarcodeFormat.UPC_E,
        BarcodeFormat.CODE_128,
        BarcodeFormat.CODE_39,
        BarcodeFormat.QR_CODE,
      ]);
      
      zxingReaderRef.current = new BrowserMultiFormatReader(hints);
      
      // Get camera stream with optimized constraints for iOS
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        }
      });
      
      streamRef.current = stream;
      videoElement.srcObject = stream;
      await videoElement.play();
      
      scanningRef.current = true;
      lastScanRef.current = '';
      
      // Wait for video to be ready
      await new Promise<void>((resolve) => {
        if (videoElement.readyState >= 2) {
          resolve();
        } else {
          videoElement.onloadeddata = () => resolve();
        }
      });
      
      const ctx = canvas.getContext('2d')!;
      let rotationIndex = 0;
      
      // Manual frame capture and decode loop
      const decodeFrame = async () => {
        if (!scanningRef.current || !zxingReaderRef.current) return;
        
        try {
          // Set canvas size to match video
          canvas.width = videoElement.videoWidth;
          canvas.height = videoElement.videoHeight;
          
          // Draw current frame
          ctx.drawImage(videoElement, 0, 0);
          
          let result = null;
          
          // Try normal orientation first
          if (rotationIndex % 2 === 0) {
            try {
              result = zxingReaderRef.current.decodeFromCanvas(canvas);
            } catch {
              // No barcode found in normal orientation
            }
          }
          
          // Try 90° rotation if normal failed
          if (!result && rotationIndex % 2 === 1) {
            try {
              // Create rotated canvas for decoding
              const rotatedCanvas = document.createElement('canvas');
              rotatedCanvas.width = canvas.height;
              rotatedCanvas.height = canvas.width;
              const rotatedCtx = rotatedCanvas.getContext('2d')!;
              rotatedCtx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
              rotatedCtx.rotate(Math.PI / 2);
              rotatedCtx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);
              
              result = zxingReaderRef.current.decodeFromCanvas(rotatedCanvas);
            } catch {
              // No barcode found in rotated orientation
            }
          }
          
          rotationIndex++;
          
          if (result) {
            handleScan(result.getText());
            return;
          }
        } catch (error) {
          // Ignore decode errors
        }
        
        // Continue scanning at ~15fps
        if (scanningRef.current) {
          setTimeout(() => requestAnimationFrame(decodeFrame), 66);
        }
      };
      
      // Start decode loop
      requestAnimationFrame(decodeFrame);
      
    } catch (error) {
      console.error('Error starting ZXing scanner:', error);
      toast.error('Impossible d\'accéder à la caméra');
      setIsScanning(false);
    }
  }, [handleScan, rotateImage90]);

  const startHtml5Scanner = useCallback(async () => {
    try {
      const container = document.getElementById(scannerContainerId);
      if (!container) {
        console.error('Scanner container not found');
        return;
      }

      html5QrCodeRef.current = new Html5Qrcode(scannerContainerId);
      
      await html5QrCodeRef.current.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 280, height: 280 },
          aspectRatio: 1.0,
          disableFlip: false,
        },
        (decodedText) => {
          handleScan(decodedText);
        },
        () => {}
      );
    } catch (error) {
      console.error('Error starting html5-qrcode:', error);
      toast.error('Impossible d\'accéder à la caméra');
      setIsScanning(false);
    }
  }, [handleScan]);

  const startScanner = useCallback(async () => {
    setIsScanning(true);
    lastScanRef.current = '';
    
    setTimeout(async () => {
      if (isIOS()) {
        await startZXingScanner();
      } else {
        await startHtml5Scanner();
      }
    }, 100);
  }, [startZXingScanner, startHtml5Scanner]);

  const handleCloseScanner = useCallback(async () => {
    await stopScanner();
    setIsScanning(false);
  }, [stopScanner]);

  const handleCloseResult = useCallback(() => {
    setShowResult(false);
    setShowAddForm(false);
    setProduct(null);
    setAddQuantity('1');
    setAddPrice('');
    lastScanRef.current = '';
  }, []);

  const handleAddToInventory = useCallback(async () => {
    if (!product || !currentCompany?.id) {
      toast.error('Données manquantes');
      return;
    }

    const quantity = parseInt(addQuantity, 10);
    const price = parseFloat(addPrice);

    if (isNaN(quantity) || quantity < 1) {
      toast.error('Quantité invalide');
      return;
    }
    if (isNaN(price) || price < 0) {
      toast.error('Prix invalide');
      return;
    }

    setIsAdding(true);
    try {
      const { error } = await supabase
        .from('products')
        .insert({
          company_id: currentCompany.id,
          name: product.name,
          barcode: product.barcode,
          image_url: product.image_url,
          quantity: quantity,
          price: price,
        });

      if (error) throw error;

      toast.success(`${product.name} ajouté à l'inventaire`);
      handleCloseResult();
    } catch (error) {
      console.error('Erreur ajout produit:', error);
      toast.error('Erreur lors de l\'ajout du produit');
    } finally {
      setIsAdding(false);
    }
  }, [product, currentCompany?.id, addQuantity, addPrice, handleCloseResult]);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, [stopScanner]);

  return (
    <>
      <Button
        onClick={startScanner}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 bg-primary hover:bg-primary/90"
        size="icon"
        aria-label="Scanner un produit"
      >
        <ScanLine className="h-6 w-6" />
      </Button>

      <Dialog open={isScanning} onOpenChange={handleCloseScanner}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <ScanLine className="h-5 w-5" />
                Scanner un produit
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="relative w-full aspect-square bg-black">
            {isIOS() ? (
              // ZXing video + canvas for iOS with rotation support
              <>
                <video 
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  playsInline
                  muted
                  autoPlay
                />
                <canvas 
                  ref={canvasRef} 
                  className="hidden" 
                />
              </>
            ) : (
              // html5-qrcode container for Android
              <div 
                id={scannerContainerId} 
                className="w-full h-full [&>div]:!border-none [&_#qr-shaded-region]:!hidden" 
              />
            )}
            
            {/* Custom overlay guide */}
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-64 h-64 relative">
                <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-primary rounded-tl-lg" />
                <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-primary rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-primary rounded-bl-lg" />
                <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-primary rounded-br-lg" />
                {/* Rotation indicator for iOS */}
                {isIOS() && (
                  <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs text-white/70 bg-black/50 px-2 py-1 rounded">
                    Horizontal ou vertical
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="p-4 pt-2">
            <p className="text-sm text-muted-foreground text-center mb-3">
              Positionnez le code-barres dans le cadre
            </p>
            <Button variant="outline" onClick={handleCloseScanner} className="w-full">
              Annuler
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showResult} onOpenChange={setShowResult}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Informations produit
            </DialogTitle>
          </DialogHeader>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : product ? (
            <div className="space-y-4">
              {product.isGeneric && (
                <div className="flex justify-center">
                  <span className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 px-2 py-1 rounded-full">
                    Données OpenFoodFacts (non enregistré)
                  </span>
                </div>
              )}

              <div className="flex justify-center">
                {product.image_url ? (
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="h-32 w-32 object-cover rounded-lg border"
                  />
                ) : (
                  <div className="h-32 w-32 bg-muted rounded-lg flex items-center justify-center">
                    <Package className="h-12 w-12 text-muted-foreground" />
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                  <span className="text-sm text-muted-foreground">Nom</span>
                  <span className="font-semibold text-right max-w-[200px]">{product.name}</span>
                </div>

                {product.brand && (
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                    <span className="text-sm text-muted-foreground">Marque</span>
                    <span className="font-semibold">{product.brand}</span>
                  </div>
                )}
                
                {!product.isGeneric && (
                  <>
                    <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm text-muted-foreground">Quantité en stock</span>
                      <span className={`font-semibold ${product.quantity <= 5 ? 'text-destructive' : 'text-green-600'}`}>
                        {product.quantity}
                      </span>
                    </div>

                    <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm text-muted-foreground">Prix</span>
                      <span className="font-semibold">{product.price.toFixed(2)} €</span>
                    </div>
                  </>
                )}

                {product.barcode && (
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                    <span className="text-sm text-muted-foreground">Code-barres</span>
                    <span className="font-mono text-sm">{product.barcode}</span>
                  </div>
                )}

                {/* Add to inventory form for generic products */}
                {product.isGeneric && showAddForm && (
                  <div className="border-t pt-4 mt-4 space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="add-quantity">Quantité initiale</Label>
                      <Input
                        id="add-quantity"
                        type="number"
                        min="1"
                        value={addQuantity}
                        onChange={(e) => setAddQuantity(e.target.value)}
                        placeholder="1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="add-price">Prix unitaire (€)</Label>
                      <Input
                        id="add-price"
                        type="number"
                        min="0"
                        step="0.01"
                        value={addPrice}
                        onChange={(e) => setAddPrice(e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Add to inventory button for generic products */}
              {product.isGeneric && !showAddForm && (
                <Button 
                  onClick={() => setShowAddForm(true)} 
                  className="w-full"
                  variant="secondary"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter à l'inventaire
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                Ce produit n'est pas enregistré dans votre inventaire.
              </p>
            </div>
          )}

          <div className="flex gap-2 mt-4">
            {showAddForm ? (
              <>
                <Button variant="outline" onClick={() => setShowAddForm(false)} className="flex-1">
                  Annuler
                </Button>
                <Button onClick={handleAddToInventory} className="flex-1" disabled={isAdding}>
                  {isAdding ? 'Ajout...' : 'Enregistrer'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={handleCloseResult} className="flex-1">
                  Fermer
                </Button>
                <Button onClick={() => { handleCloseResult(); startScanner(); }} className="flex-1">
                  Nouveau scan
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
